import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Plus, ArrowRight, Calendar } from 'lucide-react';
import { api } from '@/api/client';
import type {  TaxReturnSummary  } from '@/api/types';
import type { TaxEstimate } from '@/hooks/useTaxEstimate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { StatusPill } from '@/components/ui/status-pill';
import { AppHeader } from '@/components/AppHeader';
import { SlimFooter } from '@/components/SlimFooter';
import { formatAud, taxYearLabel, cn } from '@/lib/utils';

export const Route = createFileRoute('/returns/')({
  component: ReturnsList,
});

function ReturnsList() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['returns'],
    queryFn: () => api<TaxReturnSummary[]>('/returns'),
  });

  const create = useMutation({
    mutationFn: () => api<{ id: string }>('/returns', { method: 'POST', json: { year: 2026 } }),
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ['returns'] });
      if (resp?.id) navigate({ to: '/returns/$returnId', params: { returnId: resp.id } });
    },
  });

  return (
    <div className="flex min-h-full flex-col bg-(--color-paper)">
      <AppHeader authed crumbs={[{ label: 'My returns' }]} />
      <main className="flex-1">
        <div className="mx-auto max-w-5xl px-6 py-12">
          <header className="flex flex-col gap-5 border-b border-(--color-rule) pb-6 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
            <div className="min-w-0">
              <div className="section-ref">My returns</div>
              <h1 className="mt-1.5 font-serif text-3xl font-medium tracking-tight text-balance sm:text-4xl">
                Your tax returns
              </h1>
              <p className="mt-3 max-w-prose text-[14px] leading-relaxed text-(--color-ink-2)">
                Each financial year is a separate return. Drafts can be picked up at any
                point — your data is saved automatically.
              </p>
            </div>
            <Button
              onClick={() => create.mutate()}
              disabled={create.isPending}
              size="lg"
              className="w-full shrink-0 sm:w-auto"
            >
              <Plus className="size-4" />
              {create.isPending ? 'Creating…' : 'Start FY 2025–26'}
            </Button>
          </header>

          {(() => {
            // Backend returns 7 placeholder rows (one per year) where empty
            // years come back with id=0. Filter to real returns only.
            const real = (data ?? []).filter((r) => r.id && String(r.id) !== '0');
            if (isLoading) {
              return <p className="mt-8 text-[14px] text-(--color-ink-muted)">Loading your returns…</p>;
            }
            if (real.length === 0) {
              return <EmptyState onCreate={() => create.mutate()} pending={create.isPending} />;
            }
            return (
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {real.map((r) => (
                <li key={String(r.id)}>
                  <Link
                    to="/returns/$returnId"
                    params={{ returnId: r.id }}
                    className="group block"
                  >
                    <Card className="transition-colors group-hover:border-(--color-navy)">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <div className="flex size-11 shrink-0 items-center justify-center rounded-(--radius-sm) bg-(--color-navy-soft) text-(--color-navy)">
                              <FileText className="size-5" strokeWidth={1.6} />
                            </div>
                            <div>
                              <div className="section-ref">Tax return</div>
                              <h3 className="mt-1 font-serif text-xl font-medium text-(--color-ink)">
                                FY {taxYearLabel(r.year)}
                              </h3>
                              <div className="mt-1 inline-flex items-center gap-1.5 text-[12px] text-(--color-ink-muted)">
                                <Calendar className="size-3" />
                                {r.taxpayerName ?? '—'}
                              </div>
                            </div>
                          </div>
                          <StatusBadge status={r.status} />
                        </div>
                        <div className="mt-5 flex items-baseline justify-between border-t border-(--color-rule) pt-4">
                          <div>
                            <div className="section-ref">Estimated refund</div>
                            <RowRefund returnId={r.id} fallback={r.estimatedRefund} />
                          </div>
                          <span className="inline-flex items-center gap-1 text-[13px] font-medium text-(--color-navy) underline-offset-4 group-hover:underline">
                            Open
                            <ArrowRight className="size-3.5" />
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </li>
              ))}
            </ul>
            );
          })()}
        </div>
      </main>
      <SlimFooter />
    </div>
  );
}

/**
 * Per-return refund for the list. The `/returns` list endpoint doesn't compute a
 * refund per row (it comes back null), so we read the live tax-estimate endpoint
 * — cache-shared with the wizard/review — and fall back to any value the list did
 * provide (e.g. the mock data).
 */
function RowRefund({ returnId, fallback }: { returnId: string; fallback?: number | null }) {
  const { data } = useQuery({
    queryKey: ['returns', returnId, 'tax-estimate'],
    queryFn: () => api<TaxEstimate>(`/returns/${returnId}/tax-estimate`),
  });
  const value = data?.refundOrOwing ?? fallback ?? null;
  return (
    <div
      className={cn(
        'mt-1 text-2xl font-extrabold tabular-nums',
        value != null && value >= 0
          ? 'text-(--color-navy)'
          : value != null
            ? 'text-(--color-maroon)'
            : 'text-(--color-ink-muted)',
      )}
    >
      {value == null ? '—' : `${value >= 0 ? '+' : ''}${formatAud(value)}`}
    </div>
  );
}

function EmptyState({ onCreate, pending }: { onCreate: () => void; pending: boolean }) {
  return (
    <div className="mt-10 flex flex-col items-center rounded-(--radius) border-[1.5px] border-dashed border-(--color-rule-strong) bg-(--color-card) px-8 py-14 text-center">
      <div className="flex size-14 items-center justify-center rounded-(--radius-sm) bg-(--color-navy-soft) text-(--color-navy)">
        <FileText className="size-6" strokeWidth={1.5} />
      </div>
      <h2 className="mt-5 font-serif text-2xl font-medium tracking-tight">No returns yet</h2>
      <p className="mt-2 max-w-md text-[14px] text-(--color-ink-2)">
        Start a return for the FY 2025–26 financial year. Your assistant will pre-fill
        everything it can from the ATO before you do anything else.
      </p>
      <Button onClick={onCreate} disabled={pending} size="lg" className="mt-6">
        <Plus className="size-4" />
        {pending ? 'Creating…' : 'Start FY 2025–26 return'}
      </Button>
    </div>
  );
}

function StatusBadge({ status }: { status: TaxReturnSummary['status'] | undefined }) {
  const safe = status ?? 'draft';
  const tone = (
    {
      draft: 'warning',
      in_review: 'navy',
      approved: 'ochre',
      submitted: 'ochre',
      lodged: 'success',
    } as const
  )[safe];
  return <StatusPill tone={tone}>{safe.replace('_', ' ')}</StatusPill>;
}
